import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";

import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import Tooltip from "@mui/joy/Tooltip";

import { ShiftHistoryType } from "stores/shift_history_store";
import { UserProps } from "stores/user_store";

import useUserIdentity from "hooks/useUserIdentity";
import useCollabs from "hooks/useCollabs";
import useRoles from "hooks/useRoles";
import useUser from "hooks/useUser";
import useWorkKey from 'hooks/useWorkKey';
import useFHRequest from "hooks/useFHRequest";

import DateUI from 'components/Date';
import { TimeUI } from 'components/Date';

import { getIcon } from "utils/misc";
import IdentityAvatar from "./IdentityAvatar";



const ShiftHistoryTitle = observer(({ item }: { item: ShiftHistoryType }) => {
    if(item.associated_type.startsWith('calendar:')) {
        return <CalendarItemTitle item={item} />
    }
    if(item.collab || item.request)
        return <CollabName item={item} />;
    if(item.associated_type.startsWith("support"))
        return <SupportItemTitle item={item} />;

    return (
        <Typography level="body-sm">
            {item.display_name ? item.display_name : item.associated_type}
        </Typography>
        );

});


const CollabName = observer(({ item }: { item: ShiftHistoryType }) => {
    const { getCollab } = useCollabs();
    const { getRequest } = useFHRequest();
    const c = getCollab(item.collab);
    const r = getRequest(item.request);
    if (item.collab) {
        return (
            <Typography level="body-sm">
                <Link to={`/collab/${item.collab}`}>{c?.title || "Collab"}</Link>
            </Typography>
        );
    }
    else if (item.request) {
        return (
            <Typography level="body-sm">
                <Link to={`/request/${item.request}#${item.associated_uuid}`}>
                    {r?.title || 'Request'}
                </Link>
            </Typography>
        );
    }
    else {
        return (
        <Typography level="body-sm">
            a collab
        </Typography>
        )
    }
});


const CalendarItemTitle = observer(({ item }: { item: ShiftHistoryType }) => {
    const { getCollab } = useCollabs();
    const { getRequest } = useFHRequest();
    const c = getCollab(item.collab);
    const r = getRequest(item.request);

    if (item.collab) {
        return (
            <Typography level="body-sm">
                <Link to={`/collab/${item.collab}`}>{c?.title || "a collab"}</Link>
            </Typography>
        );
    }
    else if (item.request) {
        return (
            <Typography level="body-sm">
                <Link to={`/request/${item.request}#${item.associated_uuid}`}>
                    {r?.title || 'Request'}
                </Link>
            </Typography>
        );
    }
    else {
        return (
        <Typography level="body-sm">
            a collab
        </Typography>
        )
    }
});

const SupportItemTitle = observer(({ item }: { item: ShiftHistoryType }) => {
    const { hasRole } = useRoles();
    const { supportUserFetch } = useUser();
    const workKey = useWorkKey();
    const { currentIdentity } = useUserIdentity();
    const [fetched, setFetched] = useState(false);
    const [loadedUser, setLoadedUser] = useState({} as UserProps);
    const SudoIcon = getIcon('AdminPanelSettingsOutlined');
    const PersonSearch = getIcon('PersonSearchOutlined');
    const OtherSearch = getIcon('ManageSearchOutlined');
    const AlarmIcon = getIcon('Alarm');

    useEffect(() => {
        if(fetched) return;
        if(!workKey) return;
        if(item.associated_uuid.startsWith('us-')) {
            setFetched(true);
            supportUserFetch(currentIdentity.uuid, item.associated_uuid)
                .then((s) => {
                    setLoadedUser(s);
                });
        }
    }, [supportUserFetch, item, workKey]);


    if (hasRole('manager')) {
        if (item.associated_uuid.startsWith('sh-')) {
            return (
                <Typography
                    endDecorator={<AlarmIcon />}
                    level="body-sm">
                    <Link
                        to={`/support/user/${item.associated_uuid}`}>
                        Shift
                    </Link>
                </Typography>
            );
        }

        return (
            <Typography
                endDecorator={
                    <Tooltip
                        arrow
                        title="Only managers can see the details and/or link to this one.">
                        <SudoIcon color="warning" />
                    </Tooltip>
                }
                level="body-sm">
                {item.associated_uuid === 'not-found' ?
                    <Typography
                        endDecorator={<OtherSearch />}>
                        Not Found
                    </Typography> :
                    <Link
                        to={`/support/user/${item.associated_uuid}`}>
                        <Typography
                            endDecorator={<PersonSearch />}>{loadedUser.primary_email || 'Found User'}
                        </Typography>
                    </Link>
                }
            </Typography>
        );
    }
    else {
        return (
            <Typography level="body-sm">
                Support Action
            </Typography>
        );
    }
});

const ShiftHistoryItemContent = observer(({ action, item, icon, identity=null }: { action: string, item: ShiftHistoryType, icon: string, identity?: string | null }) => {
    const Icon = getIcon(icon);
    return (
        <Stack
            direction="row"
            alignItems="top"
            spacing={1}>

            <Icon />

            <Stack  >
                <Typography level="title-sm">
                    {action}
                </Typography>

                { identity &&
                    <Typography level="title-sm">
                        <IdentityAvatar size="name" identityKey={identity} />
                    </Typography>
                }
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <ShiftHistoryTitle item={item} />

                    <Typography level="body-xs">
                        <TimeUI ts={item.date_created} />
                    </Typography>
                </Stack>

            </Stack>
        </Stack>
    );
});

const ShiftHistoryClockItem = observer(({ item }: { item: ShiftHistoryType }) => {
    const action = item.associated_type.endsWith("start") ? "Clocked in" : "Clocked out";
    return <ShiftHistoryItemContent item={item} action={action} icon='PunchClockOutlined' />;
});


const ShiftHistoryRequestItem = observer(({ item }: { item: ShiftHistoryType }) => {
    const action = item.associated_type.endsWith("claim") ? "Claimed" : "Something";
    return <ShiftHistoryItemContent item={item} action={action} icon='AssignmentTurnedInOutlined' />;
});


const ShiftHistoryPayrollItem = observer(({ item }: { item: ShiftHistoryType }) => {
    const action = item.associated_type.endsWith("run") ? "Ran Payroll" : "Payroll Action";
        item.display_name = "Payroll Event";
    return <ShiftHistoryItemContent item={item} action={action} icon='DayOnesPayroll' />;
});



const ShiftHistoryInviteItem = observer(({ item }: { item: ShiftHistoryType }) => {
    const [_, verb] = item.associated_type.split(":")
    var action = verb;
    var identity = null;
    switch(verb) {
    case 'accept':
        action = "Joined Collab";
        break;
    case 'invite-mutual':
        action = "Invited Mutual";
        identity = item.associated_uuid;
        break;
    // case 'invite-withdraw':
    //     action = "Withdrew Invite";
    //     identity = item.associated_uuid;
    //     break;
    default:
        action = "Left Collab: " + verb;
        break;
    }

    return <ShiftHistoryItemContent identity={identity} item={item} action={action} icon='GroupAdd' />
});


const ShiftHistorySupportItem = observer(({ item }: { item: ShiftHistoryType }) => {
    const [_, verb] = item.associated_type.split(":")
    var action = verb == 'email-search' ? "User Email Search" : verb;
    var icon = "ContactMailOutlined";
    if(verb == 'sub-modify') {
        action = "Update Subscription"
    }
    if(verb == 'sub-cancel') {
        action = "Cancel Subscription"
    }
    if(verb == 'shift-edit') {
        action = "Edited Shift"
    }
    if(verb == 'request-assign') {
        action = "Assigned Request"
        icon = "AssignmentTurnedInOutlined";
    }
    console.log("Verb", verb, action);
    if(verb == 'request-update-price') {
        icon = "LocalAtm";
        action = "Updated Credits/Price"
    }
    if(verb == 'request/help/respond') {
        icon = "SupportOutlined";
        action = "Support Action Response"
    }
    if(verb == 'archive') {
        icon = "ArchiveOutlined";
        action = "Archived Request"
    }
    return <ShiftHistoryItemContent item={item} action={action} icon={icon} />
});



const ShiftHistoryShiftItem = observer(({ item }: { item: ShiftHistoryType }) => {
    let action = 'Started Shift';
    let i = Object.assign({}, item);
    //    item.display_name = 'Started Shift';
    if (i.associated_type == 'shift:end') {
        action = 'Ended Shift';
        item.display_name = 'Ended Shift';
    }
    else if (i.associated_type == 'shift:ephemeral-start') {
        action = 'Started Ephemeral Shift';
        item.display_name = 'Started';
    }
    else if (i.associated_type == 'shift:ephemeral-end') {
        action = 'Ended Ephemeral Shift';
                item.display_name = 'Ended';
    }

    //   action = item.associated_type;
    return <ShiftHistoryItemContent item={i} action={action} icon='WorkHistory' />
});

const ShiftHistoryEmailItem = observer(({ item }: { item: ShiftHistoryType }) => {
    return <ShiftHistoryItemContent item={item} action={"Sent email"} icon='ForwardToInboxOutlined' />
});



const ShiftHistoryDedicatedItem = ({ item }: { item: ShiftHistoryType }) => {
    var action = "Dedicated";
    if(item.associated_type == 'dedicated:create-collab') {
        item.display_name = "Created Collab";
        action = "Created Collab for Dedicated";
    }
    return <ShiftHistoryItemContent item={item} action={action} icon='GroupOutlined' />
};

const ShiftHistoryCalendarItem = observer(({ item }: { item: ShiftHistoryType }) => {
    var action = "Calendar";
    if(item.associated_type == 'calendar:event-create') {
        item.display_name = "Calendar Action";
        action = "Created Event";
    }
    else if(item.associated_type == 'calendar:event-delete') {
        item.display_name = "Calendar Action";
        action = "Deleted Event";
    }
    else if(item.associated_type == 'calendar:event-update') {
        item.display_name = "Calendar Action";
        action = "Updated Event";
    }
    return <ShiftHistoryItemContent item={item} action={action} icon='CalendarMonth' />
});

const ShiftHistoryMessageItem = observer(({ item }: { item: ShiftHistoryType }) => {
    var action = "Sent message";
    if(item.associated_type == 'message:qa') {
        item.display_name = "QA Message";
        action = "Sent a message to QA";
    }
    if(item.associated_type == 'message:send') {
        item.display_name = "Sent message";
        action = "Sent a message to client";
    }


    return <ShiftHistoryItemContent item={item} action={action} icon='ChatBubbleOutline' />
});

export const ShiftHistoryItemWithIdentity = observer(({item}:{item: ShiftHistoryType}) => {
    return (
        <Stack spacing={1}>
            <Typography level="body-sm">
                <IdentityAvatar size="chip" interactive="link" identityKey={item.identity} />
            </Typography>
            <ShiftHistoryItem item={item} />
        </Stack>
    );
});

export const ShiftHistoryItem = observer(({item}:{item: ShiftHistoryType}) => {
    if(item.associated_type.startsWith("clock")) {
        return <ShiftHistoryClockItem item={item} />;
    }
    else if(item.associated_type.startsWith("shift")) {
        return <ShiftHistoryShiftItem item={item} />;
    }
    else if(item.associated_type.startsWith("dedicated:")) {
        return <ShiftHistoryDedicatedItem item={item} />;
    }
    else if(item.associated_type.startsWith("message")) {
        return <ShiftHistoryMessageItem item={item} />;
    }
    else if(item.associated_type.startsWith("collab_invite")) {
        return <ShiftHistoryInviteItem item={item} />;
    }
    else if(item.associated_type.startsWith("request")) {
        return <ShiftHistoryRequestItem item={item} />;
    }
    else if(item.associated_type.startsWith("support")) {
        return <ShiftHistorySupportItem item={item} />;
    }
    else if(item.associated_type.startsWith("payroll")) {
        return <ShiftHistoryPayrollItem item={item} />;
    }
    else if(item.associated_type.startsWith("email")) {
        return <ShiftHistoryEmailItem item={item} />;
    }
    else if(item.associated_type.startsWith("calendar")) {
        return <ShiftHistoryCalendarItem item={item} />;
    }
    else {
        return (
            <div>
                <Typography level="title-md">{item.associated_type}</Typography>
                <ul>
                    <li>{item.associated_uuid}</li>
                    <li>date_created: <DateUI dateString={item.date_created} /></li>
                </ul>
            </div>
        );
    }

});
